import * as React from 'react';
import { observer } from 'mobx-react';
import { Button, Col, Row, Divider } from 'antd';

import { DropdownView } from 'src/core/ui/dropdown.view';
import { TextView } from 'src/core/ui/text.view';
import { BooleanView } from 'src/core/ui/boolean.view';
import { DateView } from 'src/core/ui/date.view';
import TimeSpam from 'src/core/ui/time-spam';
import { SearchType } from 'src/app/products/contractSimulator/parts/pax-occupancy.view';
import { OccupanciesComponent } from 'src/app/rules/rules/form/parts/executors/parts/occupancies-component';
import { RegionEditorController } from 'src/app/general/regions/parts/region-editor.controller';
import { PlaceEditorController } from 'src/app/general/places/parts/place-editor.controller';
import { ProductEditorController } from 'src/app/products/products/parts/product-editor.controller';
import SeachServiceProviderEditor from 'src/app/bookings/shared/product-search-service-providers.editor';
import { MarketEditorController } from 'src/app/general/market/parts/market-editor.controller';

interface Props {
	onFieldChange: (field: string, value: any) => void;
	values?: { [id: string]: any };
	mode: ControlMode;
	searchType: SearchType;
}

interface State {
	durationOptions: any[];
}

@observer
export class GatheringFromExternalToCacheExecutor extends React.Component<Props, State> {
	public state: State = {
		durationOptions: [],
	};

	private rangeType = [
		{ text: 'From execution time', value: 'FromNow' },
		{ text: 'Fixed', value: 'Fixed' },
	];

	public componentDidMount() {
		if (this.props.values) {
			this.props.values.validate = () => {
				if (!this.props.values["occupancies"] || JSON.parse(this.props.values["occupancies"]).length == 0) {
					return { result: false, errors: "You must provide at least one occupancy" };
				}

				if (!this.props.values['rangeType']) {
					return { result: false, errors: "You must provide a date range policy (FromNow or Fixed)" };
				}

				if (this.props.values['rangeType'] == 'Fixed') {
					const start = new Date(this.props.values['fromDate']);
					const end = new Date(this.props.values['toDate']);

					if (end.getTime() < start.getTime()) {
						return { result: false, errors: "You must provide a valid date range" };
					}
				}

				if (
					!this.props.values['sources']
					|| JSON.parse(this.props.values['sources']).length == 0
				) {
					return { result: false, errors: "You must provide at least one Source" };
				}

				if (
					!this.props.values['market']
					|| JSON.parse(this.props.values['market']).length == 0
				) {
					return { result: false, errors: "You must provide at least one Market" };
				}

				if (
					!this.props.values['durations']
					|| JSON.parse(this.props.values['durations']).length == 0
				) {
					return { result: false, errors: "You must provide at least one Duration" };
				}

				return { result: true, errors: "" };
			}
		}
	}

	public render() {
		const durationOptions = (
			<Col xs={8}>
				<DropdownView
					allowNewTags={true}
					required={true}
					label='Durations'
					model={this.props.values.durations ? JSON.parse(this.props.values.durations) : undefined}
					disabled={this.props.mode == 'display'}
					multi={true}
					onChanged={this.onChange("durations", "value")}
					keyField='value'
					textField='value'
					options={this.state.durationOptions}
				/>
			</Col>
		);

		return (
			<React.Fragment>
				<Row gutter={24}>
					<Col xs={24}>
						<p>Execute a search with the product providers and save the results into Bookem cache</p>
					</Col>
				</Row>
				<Row gutter={24}>
					<Col xs={24}>
						<TextView
							label='Description'
							rows={3}
							mode={this.props.mode}
							value={this.props.values["description"] || ''}
							onChanged={(e) => this.props.onFieldChange("description", e)}
						/>
					</Col>
				</Row>

				<Divider />

				<Row gutter={24}>
					<Col xs={10}>
						<BooleanView
							label='Force expiration'
							mode={this.props.mode}
							onChanged={e => this.props.onFieldChange("forceExpiry", e)}
							value={this.props.values["forceExpiry"] || false}
						/>
					</Col>
					{
						this.props.values["forceExpiry"] && <Col xs={14}>
							<TimeSpam
								value={this.props.values["expirationTime"]}
								showDays
								showHours
								showMinutes
								onChanged={e => this.props.values["expirationTime"] = e}
								label='Expiration time'
								mode={this.props.mode}
							/>
						</Col>
					}
				</Row>

				<Divider />

				<Row gutter={24}>
					<Col xs={10}>
						<div style={{ width: '190px' }}>
							<DropdownView
								label='Date range type'
								model={this.props.values.rangeType}
								required={true}
								disabled={this.props.mode == 'display'}
								onChanged={(e) => this.props.onFieldChange("rangeType", e.value)}
								options={this.rangeType}
								keyField='value'
								textField='text'
							/>
						</div>
					</Col>
				</Row>
				{
					this.props.values.rangeType == 'Fixed' && <React.Fragment>
						<Row gutter={24}>
							<Col xs={8}>
								<DateView
									required={true}
									label='From date'
									value={this.props.values["fromDate"]}
									format='date-time'
									onChanged={e => this.props.onFieldChange("fromDate", e)}
									mode={this.props.mode}
									dateUsage="utc"
								/>
							</Col>
							<Col xs={8}>
								<DateView
									required={true}
									label='To date'
									value={this.props.values["toDate"]}
									format='date-time'
									onChanged={e => this.props.onFieldChange("toDate", e)}
									mode={this.props.mode}
									dateUsage="utc"
								/>
							</Col>
							{durationOptions}
						</Row>
					</React.Fragment>
				}
				{
					this.props.values.rangeType == 'FromNow' && <React.Fragment>
						<Row gutter={24}>
							<Col xs={8}>
								<TimeSpam
									value={this.props.values["beforeNow"]}
									showDays
									onChanged={e => this.props.values["beforeNow"] = e}
									label='From now days delta'
									mode={this.props.mode}
								/>
							</Col>
							<Col xs={8}>
								<TimeSpam
									showDays
									value={this.props.values["afterNow"]}
									onChanged={e => this.props.values["afterNow"] = e}
									label='Days count'
									mode={this.props.mode}
								/>
							</Col>
							{durationOptions}
						</Row>
					</React.Fragment>
				}

				<Divider />

				<Row gutter={24}>
					<Col xs={12}>
						<MarketEditorController
							required={true}
							multi={true}
							mode={this.props.mode}
							value={this.props.values['market'] ? JSON.parse(this.props.values['market']) : this.props.values['market']}
							onChanged={this.onChange("market")}
							setBusy={e => { }}
							label='Market'
						/>
					</Col>
					<Col xs={12}>
						<SeachServiceProviderEditor
							required={true}
							multi={true}
							type={this.props.searchType}
							value={this.props.values.sources ? JSON.parse(this.props.values.sources) : undefined}
							mode={this.props.mode}
							label='Sources'
							textProperty='name'
							onChange={this.onChange("sources")}
						/>
						{/*<BrandEditorController
							mode={this.props.mode}
							value={this.props.values['brand']}
							onChanged={e => {
								this.props.values['brand'] = e.id
							}}
							setBusy={e => { }}
							label='Brand'
						/>*/}
					</Col>
				</Row>

				<Divider />

				<Row gutter={24}>
					<Col xs={24}>
						<OccupanciesComponent
							searchType={this.props.searchType}
							label='Occupancies combination'
							values={this.props.values["occupancies"] || '[]'}
							mode={this.props.mode}
							onValuesChange={e => this.props.values["occupancies"] = e}
						/>
					</Col>
				</Row>

				<Divider />

				{this.drawBaseOnType()}
			</React.Fragment>);
	}

	private onChange = (field: string, keyField?: string) => (e) => {
		const values = [];
		for (const i of e) {
			values.push(keyField ? i[keyField] : i.id);
		}
		this.props.onFieldChange(field, JSON.stringify(values));
		if (field === "durations") {
			this.setState({ durationOptions: e });
		}
	}

	private drawAccommodationFilter = () => {
		return (
			<React.Fragment>
				<Row gutter={24}>
					<Col xs={24}>
						<p>The search is done over all regions and places, if region and place parameters is left in blank</p>
					</Col>
				</Row>
				<Row gutter={24}>
					<Col xs={12}>
						<RegionEditorController
							multi={true}
							setBusy={e => { }}
							label='Regions'
							mode={this.props.mode}
							value={this.props.values.regions ? JSON.parse(this.props.values.regions) : undefined}
							onChanged={this.onChange("regions")}
						/>
					</Col>
					<Col xs={12}>
						<PlaceEditorController
							multi={true}
							setBusy={e => { }}
							label='Places'
							mode={this.props.mode}
							value={this.props.values.places ? JSON.parse(this.props.values.places) : undefined}
							onChanged={this.onChange("places")}
						/>
					</Col>
				</Row>
				<Row gutter={24}>
					<Row gutter={24}>
						<Col xs={24}>
							<p>The search is done over all products, if accommodation products is left in blank</p>
						</Col>
					</Row>
					<Col xs={24}>
						{this.drawProductEditor()}
					</Col>
				</Row>
			</React.Fragment>
		);
	}

	private drawProductEditor = (type?) => {
		return (
			<ProductEditorController
				label="Product"
				mode={this.props.mode}
				multi={true}
				productType={type}
				onChanged={this.onChange("products")}
				value={this.props.values.products ? JSON.parse(this.props.values.products) : undefined}
				setBusy={e => { }}
			/>
		);
	}

	private drawTransfer = () => {
		return (
			<Row gutter={24}>
				<Row gutter={24}>
					<Col xs={24}>
						<p>The search is done over all products, If transfer products is left in blank</p>
					</Col>
				</Row>
				<Col xs={24}>
					{this.drawProductEditor(3)}
				</Col>
			</Row>
		);
	}

	private drawGeneral = () => {
		return (
			<Row gutter={24}>
				<Row gutter={24}>
					<Col xs={24}>
						<p>The search is done over all products, If general products is left in blank</p>
					</Col>
				</Row>
				<Col xs={24}>
					{this.drawProductEditor(0)}
				</Col>
			</Row>
		);
	}

	private drawCarRent = () => {
		return (
			<Row gutter={24}>
				<Row gutter={24}>
					<Col xs={24}>
						<p>The search is done over all products, If car rent products is left in blank</p>
					</Col>
				</Row>
				<Col xs={24}>
					{this.drawProductEditor(2)}
				</Col>
			</Row>
		);
	}

	private drawBaseOnType = () => {
		switch (this.props.searchType) {
			case 'Accommodation':
				return this.drawAccommodationFilter();
			case 'CarRent':
				return this.drawCarRent();
			case 'General':
				return this.drawGeneral();
			case 'Transfer':
				return this.drawTransfer();
			default:
				throw new Error("Unknown product type search!");
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/rules/rules/form/parts/executors/gather-from-external-tocache.executor.tsx